import React, { useMemo, useState } from 'react';

import {
    Autocomplete,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    TextField
} from '@mui/material';
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded';
import EmailRoundedIcon from '@mui/icons-material/EmailRounded';
import EventRepeatRoundedIcon from '@mui/icons-material/EventRepeatRounded';
import GavelRoundedIcon from '@mui/icons-material/GavelRounded';
import HandshakeRoundedIcon from '@mui/icons-material/HandshakeRounded';
import LocationOnRoundedIcon from '@mui/icons-material/LocationOnRounded';
import LockResetRoundedIcon from '@mui/icons-material/LockResetRounded';
import MarkEmailReadRoundedIcon from '@mui/icons-material/MarkEmailReadRounded';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import PhoneRoundedIcon from '@mui/icons-material/PhoneRounded';
import PlaylistRemoveRoundedIcon from '@mui/icons-material/PlaylistRemoveRounded';
import PowerSettingsNewRoundedIcon from '@mui/icons-material/PowerSettingsNewRounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import SwapHorizRoundedIcon from '@mui/icons-material/SwapHorizRounded';
import UndoRoundedIcon from '@mui/icons-material/UndoRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';

import DayDatePicker from '../../components/dayDatePicker';
import { formatBackendDatePtBr, formatMonthShortPtBr, isValidDate, parseBackendDate } from '../../utils/dateUtils';
import { distractCategories } from '../../utils/distractCategories';
import { maskCpfCnpj } from '../../utils/maskCpfCnpj';
import { formatDecimal } from '../../utils/numberUtils';
import { CNPJ_DIGITS_LENGTH, formatCurrency, getDigits } from '../../utils/stringFormat';
import {
    CONTACT_UPDATE_FIELDS,
    EMPTY_CONTACT_UPDATE_FORM,
    RECEIVABLE_CANCEL_REASONS,
    buildFinanceSummary,
    buildPhoneLabel,
    getConsorcioBadge,
    getFinanceStatusTone,
    getProtocolStatusTone,
    getReceivableDisplayStatus,
    getTotalPages,
    isOpenAmount
} from './crmClientData';

const dash = (value) => {
    if (value === null || value === undefined) return '—';

    const text = String(value).trim();

    return text === '' ? '—' : text;
};

const dateOrDash = (value) => formatBackendDatePtBr(value, '—');
const monthOrDash = (value) => formatMonthShortPtBr(value) || '—';
const currencyOrDash = (value) => (value === null || value === undefined ? '—' : formatCurrency(value));

export const ClientPanelState = ({ isLoading, hasMatricula, isEmpty, emptyLabel }) => {
    if (!hasMatricula) {
        return (
            <div className='crm-client-state'>
                <span className='crm-client-state-title'>Lead sem matrícula</span>
                <span className='crm-client-state-copy'>
                    Esta aba usa os dados do cliente. Assim que o lead virar cliente e receber uma matrícula, as informações aparecem aqui.
                </span>
            </div>
        );
    }

    if (isLoading) {
        return (
            <div className='crm-client-state'>
                <CircularProgress size={26} className='crm-client-state-spinner' />
                <span className='crm-client-state-copy'>Carregando dados do cliente...</span>
            </div>
        );
    }

    if (isEmpty) {
        return (
            <div className='crm-client-state'>
                <span className='crm-client-state-title'>Nada por aqui</span>
                <span className='crm-client-state-copy'>{emptyLabel}</span>
            </div>
        );
    }

    return null;
};

export const Pill = ({ tone = 'is-neutral', children }) => (
    <span className={`crm-client-pill ${tone}`}>{children}</span>
);

export const Field = ({ label, value, wide = false }) => (
    <div className={`crm-client-field${wide ? ' is-wide' : ''}`}>
        <span className='crm-client-field-label'>{label}</span>
        <span className='crm-client-field-value'>{value}</span>
    </div>
);

export const SummaryTile = ({ icon: Icon, label, value, hint, tone = '' }) => (
    <div className={`crm-client-tile ${tone}`}>
        <span className='crm-client-tile-icon'><Icon fontSize='inherit' /></span>
        <div className='crm-client-tile-body'>
            <span className='crm-client-tile-label'>{label}</span>
            <strong className='crm-client-tile-value'>{value}</strong>
            {hint && <span className='crm-client-tile-hint'>{hint}</span>}
        </div>
    </div>
);

const DataTable = ({ columns, rows, renderCell, getRowKey, isScrollable = false }) => (
    <div className={`crm-client-table-wrapper${isScrollable ? ' is-scrollable' : ''}`}>
        <table className='crm-client-table'>
            <thead>
                <tr>
                    {columns.map((column) => (
                        <th key={column.field} className={column.align ? `is-${column.align}` : undefined}>
                            {column.label}
                        </th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {rows.map((row, index) => (
                    <tr key={getRowKey(row, index)}>
                        {columns.map((column) => (
                            <td key={column.field} className={column.align ? `is-${column.align}` : undefined}>
                                {renderCell(column, row)}
                            </td>
                        ))}
                    </tr>
                ))}
            </tbody>
        </table>
    </div>
);

const UnitLink = ({ ligacao, isAtual }) => {
    const consorcio = getConsorcioBadge(ligacao.no_consorcio);

    return (
        <div className={`crm-client-link${isAtual ? ' is-atual' : ''}`}>
            <div className='crm-client-link-head'>
                <span className='crm-client-link-cc'>CC {dash(ligacao.cc)}</span>

                <span className='crm-client-link-pills'>
                    <Pill tone={consorcio.tone}>{consorcio.label}</Pill>
                    {isAtual && <Pill tone={ligacao.ativa ? 'is-positive' : 'is-muted'}>
                        {ligacao.ativa ? 'Vigente' : 'Atual'}
                    </Pill>}
                </span>
            </div>

            <div className='crm-client-field-grid is-compact'>
                <Field label='Contrato' value={dash(ligacao.contrato)} />
                <Field label='Documento' value={dash(ligacao.documento)} />
                {ligacao.data_troca && <Field label='Troca' value={dateOrDash(ligacao.data_troca)} />}
            </div>
        </div>
    );
};

const getDebtNoticeDates = (infoDebitos) => (Array.isArray(infoDebitos) ? infoDebitos : [])
    .map((value) => ({ value, date: parseBackendDate(value) }))
    .filter(({ date }) => isValidDate(date))
    .sort((a, b) => a.date - b.date);

const UnitDebtTimeline = ({ notices }) => (
    <div className='crm-client-debts'>
        <span className='crm-client-event-head'>
            <WarningAmberRoundedIcon fontSize='inherit' />
            {notices.length > 1 ? `Avisos de débito · ${notices.length}` : 'Aviso de débito'}
        </span>

        <ol className='crm-client-debt-timeline'>
            {notices.map((notice, index) => (
                <li
                    key={`${notice.value}-${index}`}
                    className={`crm-client-debt-step${index === notices.length - 1 ? ' is-latest' : ''}`}
                >
                    <span className='crm-client-debt-dot' aria-hidden='true' />
                    <span className='crm-client-debt-label'>{index + 1}º aviso</span>
                    <span className='crm-client-debt-date'>{dateOrDash(notice.value)}</span>
                </li>
            ))}
        </ol>
    </div>
);

const UnitDeactivateDialog = ({ unit, isBusy, onClose, onConfirm }) => (
    <Dialog open className='crm-client-dialog' onClose={isBusy ? undefined : onClose}>
        <DialogTitle>Inativar unidade</DialogTitle>
        <DialogContent className='crm-client-dialog-content'>
            <DialogContentText>
                A unidade {dash(unit.unidade)}, CC {dash(unit.cc)}, será inativada. Confirma?
            </DialogContentText>
        </DialogContent>
        <DialogActions>
            <Button onClick={onConfirm} disabled={isBusy}>
                {isBusy ? 'Inativando...' : 'Confirmar'}
            </Button>
            <Button onClick={onClose} disabled={isBusy}>Voltar</Button>
        </DialogActions>
    </Dialog>
);

export const ClientDistractDialog = ({
    clientName,
    documentOptions,
    unitNames,
    isLoadingUnits,
    isBusy,
    onClose,
    onConfirm
}) => {
    const [motivo, setMotivo] = useState('');
    const [data, setData] = useState(null);
    const [cpfCnpj, setCpfCnpj] = useState('');
    const [representativeName, setRepresentativeName] = useState('');
    const [representativeCpf, setRepresentativeCpf] = useState('');

    const selectedDocument = cpfCnpj || documentOptions[0] || '';
    const requiresRepresentative = getDigits(selectedDocument).length === CNPJ_DIGITS_LENGTH;
    const hasRepresentative = !requiresRepresentative ||
        (representativeName.trim() !== '' && representativeCpf.length === 14);
    const canConfirm = !isBusy && !isLoadingUnits && unitNames.length > 0 &&
        selectedDocument !== '' && motivo !== '' && isValidDate(data) && hasRepresentative;

    const unitsLabel = isLoadingUnits
        ? 'Carregando unidades do cliente...'
        : unitNames.length === 0
            ? 'Nenhuma unidade ativa encontrada para distratar.'
            : `${unitNames.length} ${unitNames.length === 1 ? 'unidade será distratada' : 'unidades serão distratadas'}: ${unitNames.join(', ')}.`;

    return (
        <Dialog open className='crm-client-dialog' onClose={isBusy ? undefined : onClose}>
            <DialogTitle>Distrato do cliente</DialogTitle>
            <DialogContent className='crm-client-dialog-content'>
                <div className='crm-client-dialog-warning' role='alert'>
                    <WarningAmberRoundedIcon fontSize='inherit' />
                    <span>
                        O distrato vale para o cliente inteiro e encerra o contrato de todas as unidades.
                        Para retirar só algumas unidades, gere um aditivo de exclusão.
                    </span>
                </div>
                <DialogContentText>{dash(clientName)}</DialogContentText>
                <DialogContentText>{unitsLabel}</DialogContentText>
                <FormControl required fullWidth disabled={isLoadingUnits || documentOptions.length <= 1}>
                    <InputLabel id='labelDocumentoDistrato'>CPF/CNPJ</InputLabel>
                    <Select
                        labelId='labelDocumentoDistrato'
                        label='CPF/CNPJ'
                        value={documentOptions.includes(selectedDocument) ? selectedDocument : ''}
                        onChange={(event) => setCpfCnpj(event.target.value)}
                    >
                        {documentOptions.map((option) => (
                            <MenuItem key={option} value={option}>{option}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControl required fullWidth>
                    <InputLabel id='labelMotivoDistrato'>Motivo</InputLabel>
                    <Select
                        labelId='labelMotivoDistrato'
                        label='Motivo'
                        value={motivo}
                        onChange={(event) => setMotivo(event.target.value)}
                    >
                        {distractCategories.map((category) => (
                            <MenuItem key={category.value} value={category.value}>{category.label}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <DayDatePicker
                    label='Data do distrato'
                    minDate={new Date('2023-01-02')}
                    value={data}
                    onChange={setData}
                />
                {requiresRepresentative && (
                    <>
                        <TextField
                            required
                            label='Nome do representante'
                            value={representativeName}
                            onChange={(event) => setRepresentativeName(event.target.value)}
                        />
                        <TextField
                            required
                            label='CPF do representante'
                            value={representativeCpf}
                            inputProps={{ maxLength: 14 }}
                            onChange={(event) => setRepresentativeCpf(maskCpfCnpj(event.target.value).slice(0, 14))}
                        />
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={() => onConfirm({
                        motivo,
                        data,
                        cpfCnpj: selectedDocument,
                        representativeName: requiresRepresentative ? representativeName.trim() : '',
                        representativeCpf: requiresRepresentative ? representativeCpf : ''
                    })}
                    disabled={!canConfirm}
                >
                    {isBusy ? 'Distratando...' : 'Confirmar distrato'}
                </Button>
                <Button onClick={onClose} disabled={isBusy}>Voltar</Button>
            </DialogActions>
        </Dialog>
    );
};

export const ClientUnitsPanel = ({ units, canDeactivate = false, onDeactivate, busyUnitId = null }) => {
    const [unitToDeactivate, setUnitToDeactivate] = useState(null);

    const closeDeactivateDialog = () => setUnitToDeactivate(null);

    const confirmDeactivate = async () => {
        if (await onDeactivate(unitToDeactivate)) closeDeactivateDialog();
    };

    return (
        <div className='crm-client-units'>
            {unitToDeactivate && (
                <UnitDeactivateDialog
                    unit={unitToDeactivate}
                    isBusy={busyUnitId !== null}
                    onClose={closeDeactivateDialog}
                    onConfirm={confirmDeactivate}
                />
            )}

            {units.map((unit) => {
                const historico = unit.historico ?? [];
                const hasDistrato = Boolean(unit.distrato_data || unit.distrato_motivo);
                const debtNotices = getDebtNoticeDates(unit.info_debitos);
                const consorcio = getConsorcioBadge(unit.no_consorcio);

                return (
                    <article key={unit.unidade_id} className={`crm-client-unit${hasDistrato ? ' has-distrato' : ''}`}>
                        <header className='crm-client-unit-header'>
                            <div className='crm-client-unit-identity'>
                                <h4 className='crm-client-unit-title'>{dash(unit.unidade)}</h4>
                                <span className='crm-client-unit-cc'>
                                    Instalação {dash(unit.instalacao)}
                                    {historico.length > 1 && ` · ${historico.length} ligações`}
                                </span>
                            </div>

                            <div className='crm-client-unit-flags'>
                                <Pill tone={unit.ativa ? 'is-positive' : 'is-muted'}>{unit.ativa ? 'Ativa' : 'Inativa'}</Pill>
                                <Pill tone={consorcio.tone}>{consorcio.label}</Pill>
                                {unit.suspenso && <Pill tone='is-danger'>Suspensa</Pill>}
                                {unit.geradora && <Pill tone='is-info'>Geradora</Pill>}
                                {unit.autoconsumo && <Pill tone='is-info'>Autoconsumo</Pill>}

                                {canDeactivate && unit.ativa && unit.cc && (
                                    <button
                                        type='button'
                                        className='crm-client-row-action is-danger'
                                        onClick={() => setUnitToDeactivate(unit)}
                                        disabled={busyUnitId !== null}
                                    >
                                        <PowerSettingsNewRoundedIcon fontSize='inherit' />
                                        <span>Inativar</span>
                                    </button>
                                )}
                            </div>
                        </header>

                        <p className='crm-client-unit-address'>
                            <LocationOnRoundedIcon fontSize='inherit' />
                            {dash(unit.endereco)}
                        </p>

                        <div className='crm-client-field-grid'>
                            <Field label='UC' value={dash(unit.uc)} />
                            <Field label='Categoria' value={dash(unit.categoria)} />
                            <Field label='Distribuidora' value={dash(unit.distribuidora)} />
                            <Field
                                label='Contratado'
                                value={unit.contratado === null || unit.contratado === undefined
                                    ? '—'
                                    : `${formatDecimal(unit.contratado)} kWh`}
                            />
                            {unit.data_troca && (
                                <Field label='Troca de titularidade' value={dateOrDash(unit.data_troca)} />
                            )}
                        </div>

                        {debtNotices.length > 0 && <UnitDebtTimeline notices={debtNotices} />}

                        {hasDistrato && (
                            <div className='crm-client-distrato'>
                                <span className='crm-client-event-head'>
                                    <GavelRoundedIcon fontSize='inherit' />
                                    Distrato
                                </span>
                                <div className='crm-client-field-grid is-compact'>
                                    <Field label='Data' value={dateOrDash(unit.distrato_data)} />
                                    <Field label='Motivo' value={dash(unit.distrato_motivo)} wide />
                                </div>
                            </div>
                        )}

                        {historico.length > 0 && (
                            <div className='crm-client-unit-links'>
                                <span className='crm-client-event-head'>
                                    <SwapHorizRoundedIcon fontSize='inherit' />
                                    {historico.length > 1 ? 'Ligações e trocas de titularidade' : 'Ligação'}
                                </span>

                                {/* O backend manda do mais antigo para o mais recente, que e a
                                    ordem em que a troca faz sentido de ler. */}
                                {historico.map((ligacao) => (
                                    <UnitLink
                                        key={ligacao.cc_id}
                                        ligacao={ligacao}
                                        isAtual={ligacao.cc_id === unit.cc_id}
                                    />
                                ))}
                            </div>
                        )}
                    </article>
                );
            })}
        </div>
    );
};

const SELECT_ALL_UNITS = 'Selecionar todas';

export const ClientContactForm = ({ unitOptions, isSaving, onCancel, onSave }) => {
    const [units, setUnits] = useState([]);
    const [form, setForm] = useState(EMPTY_CONTACT_UPDATE_FORM);

    const autocompleteOptions = unitOptions.length > 0 ? [SELECT_ALL_UNITS, ...unitOptions] : [];

    const selectUnits = (event, values) => {
        setUnits(values.includes(SELECT_ALL_UNITS) ? unitOptions : values);
    };

    const updateField = (key, value) => setForm((current) => ({ ...current, [key]: value }));

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (isSaving) return;

        if (await onSave(units, form)) {
            setUnits([]);
            setForm(EMPTY_CONTACT_UPDATE_FORM);
        }
    };

    return (
        <form className='crm-lead-details-contact-form' onSubmit={handleSubmit}>
            <p className='crm-lead-details-contact-form-copy'>
                Escolha as unidades e preencha apenas o que muda. Os campos em branco ficam como estão.
            </p>

            <Autocomplete
                multiple
                disableCloseOnSelect
                limitTags={6}
                value={units}
                options={autocompleteOptions}
                onChange={selectUnits}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        className='crm-lead-details-text-field crm-lead-details-text-field-editable'
                        label='Unidades'
                    />
                )}
            />

            <div className='crm-lead-details-grid'>
                {CONTACT_UPDATE_FIELDS.map((field) => (
                    <div key={field.key} className={`crm-lead-details-field ${field.span}`}>
                        <TextField
                            className='crm-lead-details-text-field crm-lead-details-text-field-editable'
                            label={field.label}
                            value={form[field.key]}
                            onChange={(event) => updateField(field.key, event.target.value)}
                            disabled={isSaving}
                            fullWidth
                        />
                    </div>
                ))}
            </div>

            <div className='crm-lead-details-contact-form-actions'>
                <button
                    type='button'
                    className='crm-lead-details-button crm-lead-details-button-warning is-compact'
                    onClick={onCancel}
                    disabled={isSaving}
                >
                    <UndoRoundedIcon fontSize='inherit' />
                    <span>Cancelar</span>
                </button>
                <button
                    type='submit'
                    className='crm-lead-details-button crm-lead-details-button-primary is-compact'
                    disabled={isSaving}
                >
                    <SaveRoundedIcon fontSize='inherit' />
                    <span>{isSaving ? 'Salvando...' : 'Salvar contato'}</span>
                </button>
            </div>
        </form>
    );
};

export const ClientPortalLoginForm = ({
    currentEmail,
    isSavingPassword,
    isSavingEmail,
    onCancel,
    onChangePassword,
    onChangeEmail
}) => {
    const [email, setEmail] = useState('');

    const isSaving = isSavingPassword || isSavingEmail;

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (isSaving) return;

        if (await onChangeEmail(email)) {
            setEmail('');
        }
    };

    return (
        <form className='crm-lead-details-contact-form crm-lead-details-portal-form' onSubmit={handleSubmit}>
            <p className='crm-lead-details-contact-form-copy'>
                E-mail de acesso atual: {currentEmail || 'não cadastrado'}. Redefinir a senha e trocar o e-mail são ações independentes.
            </p>

            <div className='crm-lead-details-grid'>
                <div className='crm-lead-details-field is-medium'>
                    <TextField
                        className='crm-lead-details-text-field crm-lead-details-text-field-editable'
                        label='Novo e-mail do portal'
                        value={email}
                        onChange={(event) => setEmail(event.target.value)}
                        disabled={isSaving}
                        fullWidth
                    />
                </div>
            </div>

            <div className='crm-lead-details-contact-form-actions'>
                <button
                    type='button'
                    className='crm-lead-details-button crm-lead-details-button-warning is-compact'
                    onClick={onCancel}
                    disabled={isSaving}
                >
                    <UndoRoundedIcon fontSize='inherit' />
                    <span>Cancelar</span>
                </button>
                <button
                    type='button'
                    className='crm-lead-details-button crm-lead-details-button-secondary is-compact'
                    onClick={onChangePassword}
                    disabled={isSaving}
                >
                    <LockResetRoundedIcon fontSize='inherit' />
                    <span>{isSavingPassword ? 'Enviando...' : 'Redefinir senha'}</span>
                </button>
                <button
                    type='submit'
                    className='crm-lead-details-button crm-lead-details-button-primary is-compact'
                    disabled={isSaving || email.trim() === ''}
                >
                    <MarkEmailReadRoundedIcon fontSize='inherit' />
                    <span>{isSavingEmail ? 'Salvando...' : 'Alterar e-mail'}</span>
                </button>
            </div>
        </form>
    );
};

export const ClientContactsPanel = ({ contacts }) => {
    const cliente = contacts?.cliente;
    const contatos = contacts?.contatos ?? [];

    return (
        <div className='crm-client-stack'>
            {cliente && (
                <section className='crm-client-block'>
                    <h4 className='crm-client-block-title'>
                        <PersonRoundedIcon fontSize='inherit' />
                        Cliente
                    </h4>

                    <div className='crm-client-field-grid'>
                        <Field label='Matrícula' value={dash(cliente.cliente_id)} />
                        <Field label='Cliente' value={dash(cliente.cliente)} wide />
                        <Field label='E-mail do portal' value={dash(cliente.email_portal)} wide />
                        <Field
                            label='Situação'
                            value={<Pill tone={cliente.ativo ? 'is-positive' : 'is-muted'}>{cliente.ativo ? 'Ativo' : 'Inativo'}</Pill>}
                        />
                        <Field label='Início' value={dateOrDash(cliente.inicio)} />
                        <Field label='Fim' value={dateOrDash(cliente.fim)} />
                    </div>
                </section>
            )}

            {contatos.length > 0 && (
                <section className='crm-client-block'>
                    <h4 className='crm-client-block-title'>
                        <EmailRoundedIcon fontSize='inherit' />
                        Contatos das unidades
                        <span className='crm-client-block-count'>{contatos.length}</span>
                    </h4>

                    <div className='crm-client-contact-grid'>
                        {contatos.map((contato) => (
                            <article key={contato.contato_id} className='crm-client-contact'>
                                <h5 className='crm-client-contact-name'>{dash(contato.nome)}</h5>
                                <span className='crm-client-contact-unit'>{dash(contato.unidade)}</span>

                                <div className='crm-client-contact-lines'>
                                    <span className='crm-client-contact-line'>
                                        <DescriptionRoundedIcon fontSize='inherit' />
                                        {dash(contato.cpf_cnpj)}
                                    </span>
                                    {buildPhoneLabel(contato.ddd, contato.telefone) && (
                                        <span className='crm-client-contact-line'>
                                            <PhoneRoundedIcon fontSize='inherit' />
                                            {buildPhoneLabel(contato.ddd, contato.telefone)}
                                        </span>
                                    )}
                                    {buildPhoneLabel(contato.ddd_dois, contato.telefone_dois) && (
                                        <span className='crm-client-contact-line'>
                                            <PhoneRoundedIcon fontSize='inherit' />
                                            {buildPhoneLabel(contato.ddd_dois, contato.telefone_dois)}
                                        </span>
                                    )}
                                    {contato.email && (
                                        <span className='crm-client-contact-line'>
                                            <EmailRoundedIcon fontSize='inherit' />
                                            {contato.email}
                                        </span>
                                    )}
                                    {contato.email_dois && (
                                        <span className='crm-client-contact-line'>
                                            <EmailRoundedIcon fontSize='inherit' />
                                            {contato.email_dois}
                                        </span>
                                    )}
                                </div>
                            </article>
                        ))}
                    </div>
                </section>
            )}
        </div>
    );
};

const protocolColumns = [
    { field: 'protocolo', label: 'Protocolo' },
    { field: 'natureza', label: 'Natureza' },
    { field: 'descricao', label: 'Descrição' },
    { field: 'status', label: 'Status' },
    { field: 'responsavel', label: 'Responsável' },
    { field: 'data_inicio', label: 'Início' },
    { field: 'prazo', label: 'Prazo', align: 'right' },
    { field: 'conclusao', label: 'Conclusão' },
    { field: 'unidade', label: 'Unidade' },
    { field: 'valor', label: 'Valor', align: 'right' }
];

const renderProtocolCell = (column, row) => {
    if (column.field === 'status') {
        return <Pill tone={getProtocolStatusTone(row.status)}>{dash(row.status)}</Pill>;
    }

    if (column.field === 'data_inicio' || column.field === 'conclusao') return dateOrDash(row[column.field]);
    if (column.field === 'valor') return currencyOrDash(row.valor);
    if (column.field === 'prazo') return row.prazo === null || row.prazo === undefined ? '—' : `${row.prazo} d`;

    return dash(row[column.field]);
};

export const ClientProtocolsPanel = ({ protocols }) => (
    <section className='crm-client-block'>
        <h4 className='crm-client-block-title'>
            <DescriptionRoundedIcon fontSize='inherit' />
            Protocolos
            <span className='crm-client-block-count'>{protocols.length}</span>
        </h4>

        <DataTable
            columns={protocolColumns}
            rows={protocols}
            renderCell={renderProtocolCell}
            getRowKey={(row) => row.protocolo_id}
        />
    </section>
);

const receivableColumns = [
    { field: 'vencimento', label: 'Vencimento' },
    { field: 'periodo', label: 'Período' },
    { field: 'cc', label: 'CC' },
    { field: 'status', label: 'Status' },
    { field: 'consumo', label: 'Consumo', align: 'right' },
    { field: 'economia', label: 'Economia', align: 'right' },
    { field: 'desconto_indicacao', label: 'Desconto indicação', align: 'right' },
    { field: 'valor', label: 'Valor', align: 'right' },
    { field: 'acoes', label: 'Ações' }
];

const ReceivableRowActions = ({ row, isBusy, onExtend, onCancel }) => (
    <div className='crm-client-row-actions'>
        <button
            type='button'
            className='crm-client-row-action'
            onClick={() => onExtend(row)}
            disabled={isBusy}
        >
            <EventRepeatRoundedIcon fontSize='inherit' />
            <span>Prorrogar</span>
        </button>
        <button
            type='button'
            className='crm-client-row-action is-danger'
            onClick={() => onCancel(row)}
            disabled={isBusy}
        >
            <PlaylistRemoveRoundedIcon fontSize='inherit' />
            <span>Cancelar</span>
        </button>
    </div>
);

const renderReceivableCell = (column, row, actions) => {
    if (column.field === 'acoes') {
        if (!isOpenAmount(row.status)) return '—';

        return (
            <ReceivableRowActions
                row={row}
                isBusy={actions.busyLancamento !== null}
                onExtend={actions.onExtend}
                onCancel={actions.onCancel}
            />
        );
    }

    if (column.field === 'status') {
        const status = getReceivableDisplayStatus(row);

        return <Pill tone={getFinanceStatusTone(status)}>{dash(status)}</Pill>;
    }

    if (column.field === 'vencimento') return dateOrDash(row.vencimento);
    if (column.field === 'periodo') return monthOrDash(row.periodo);
    if (['valor', 'economia', 'desconto_indicacao'].includes(column.field)) return currencyOrDash(row[column.field]);
    if (column.field === 'consumo') {
        return row.consumo === null || row.consumo === undefined ? '—' : `${formatDecimal(row.consumo)} kWh`;
    }

    return dash(row[column.field]);
};

const ReceivableCancelDialog = ({ row, isBusy, onClose, onConfirm }) => {
    const [obs, setObs] = useState('');

    return (
        <Dialog open className='crm-client-dialog' onClose={isBusy ? undefined : onClose}>
            <DialogTitle>Cancelar boleto</DialogTitle>
            <DialogContent className='crm-client-dialog-content'>
                <DialogContentText>
                    O lançamento {dash(row.codigo_lancamento)}, com vencimento em {dateOrDash(row.vencimento)} e valor de {currencyOrDash(row.valor)}, será cancelado. Escolha a observação do cancelamento.
                </DialogContentText>
                <FormControl required fullWidth>
                    <InputLabel id='labelObsCancelamento'>Observação</InputLabel>
                    <Select
                        labelId='labelObsCancelamento'
                        label='Observação'
                        value={obs}
                        onChange={(event) => setObs(event.target.value)}
                    >
                        {RECEIVABLE_CANCEL_REASONS.map((reason) => (
                            <MenuItem key={reason.value} value={reason.value}>{reason.label}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => onConfirm(obs)} disabled={isBusy || obs === ''}>
                    {isBusy ? 'Cancelando...' : 'Confirmar'}
                </Button>
                <Button onClick={onClose} disabled={isBusy}>Voltar</Button>
            </DialogActions>
        </Dialog>
    );
};

const ReceivableExtendDialog = ({ row, isBusy, onClose, onConfirm }) => {
    const [vencimento, setVencimento] = useState(null);

    return (
        <Dialog open className='crm-client-dialog' onClose={isBusy ? undefined : onClose}>
            <DialogTitle>Prorrogar boleto</DialogTitle>
            <DialogContent className='crm-client-dialog-content'>
                <DialogContentText>
                    O lançamento {dash(row.codigo_lancamento)} vence em {dateOrDash(row.vencimento)}. Informe o novo vencimento.
                </DialogContentText>
                <DayDatePicker
                    label='Novo vencimento'
                    value={vencimento}
                    onChange={setVencimento}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={() => onConfirm(vencimento)} disabled={isBusy || !isValidDate(vencimento)}>
                    {isBusy ? 'Prorrogando...' : 'Confirmar'}
                </Button>
                <Button onClick={onClose} disabled={isBusy}>Voltar</Button>
            </DialogActions>
        </Dialog>
    );
};

const simpleFinanceColumns = [
    { field: 'vencimento', label: 'Vencimento' },
    { field: 'codigo_lancamento', label: 'Lançamento' },
    { field: 'status', label: 'Status' },
    { field: 'valor', label: 'Valor', align: 'right' }
];

const agreementColumns = [
    { field: 'vencimento', label: 'Vencimento' },
    { field: 'codigo_lancamento', label: 'Lançamento' },
    { field: 'parcela', label: 'Parcela', align: 'right' },
    { field: 'status', label: 'Status' },
    { field: 'valor', label: 'Valor', align: 'right' }
];

const renderSimpleFinanceCell = (column, row) => {
    if (column.field === 'status') {
        return <Pill tone={getFinanceStatusTone(row.status)}>{dash(row.status)}</Pill>;
    }

    if (column.field === 'vencimento') return dateOrDash(row.vencimento);
    if (column.field === 'valor') return currencyOrDash(row.valor);

    return dash(row[column.field]);
};

const FinancePager = ({ page, total, pageSize, onPageChange }) => {
    const totalPages = getTotalPages(total, pageSize);

    if (totalPages <= 1) return null;

    const primeiro = (page - 1) * pageSize + 1;
    const ultimo = Math.min(page * pageSize, total);

    return (
        <div className='crm-client-pager'>
            <span className='crm-client-pager-label'>
                {primeiro}–{ultimo} de {total}
            </span>

            <div className='crm-client-pager-controls'>
                <button
                    type='button'
                    className='crm-client-more-button'
                    onClick={() => onPageChange(page - 1)}
                    disabled={page <= 1}
                >
                    Anterior
                </button>
                <span className='crm-client-pager-page'>Página {page} de {totalPages}</span>
                <button
                    type='button'
                    className='crm-client-more-button'
                    onClick={() => onPageChange(page + 1)}
                    disabled={page >= totalPages}
                >
                    Próxima
                </button>
            </div>
        </div>
    );
};

export const ClientFinancePanel = ({
    finance,
    onReceivablesPageChange,
    onCancelReceivable,
    onExtendReceivable,
    busyLancamento = null
}) => {
    const summary = useMemo(() => buildFinanceSummary(finance), [finance]);
    const [receivableAction, setReceivableAction] = useState(null);
    const [statusFilter, setStatusFilter] = useState([]);

    const receivables = finance?.contas_a_receber ?? { items: [], total: 0, page: 1, page_size: 0 };
    const penalties = finance?.multas ?? [];
    const agreements = finance?.acordos ?? [];

    const statusOptions = useMemo(
        () => [...new Set(receivables.items.map(getReceivableDisplayStatus).filter(Boolean))].sort(),
        [receivables.items]
    );

    const visibleReceivables = useMemo(
        () => (statusFilter.length === 0
            ? receivables.items
            : receivables.items.filter((row) => statusFilter.includes(getReceivableDisplayStatus(row)))),
        [receivables.items, statusFilter]
    );

    const closeReceivableAction = () => setReceivableAction(null);

    const confirmCancel = async (obs) => {
        if (await onCancelReceivable(receivableAction.row.codigo_lancamento, obs)) closeReceivableAction();
    };

    const confirmExtend = async (vencimento) => {
        if (await onExtendReceivable(receivableAction.row.codigo_lancamento, vencimento)) closeReceivableAction();
    };

    const receivableActions = {
        busyLancamento,
        onCancel: (row) => setReceivableAction({ type: 'cancelar', row }),
        onExtend: (row) => setReceivableAction({ type: 'prorrogar', row })
    };

    return (
        <div className='crm-client-stack'>
            {receivableAction?.type === 'cancelar' && (
                <ReceivableCancelDialog
                    row={receivableAction.row}
                    isBusy={busyLancamento !== null}
                    onClose={closeReceivableAction}
                    onConfirm={confirmCancel}
                />
            )}

            {receivableAction?.type === 'prorrogar' && (
                <ReceivableExtendDialog
                    row={receivableAction.row}
                    isBusy={busyLancamento !== null}
                    onClose={closeReceivableAction}
                    onConfirm={confirmExtend}
                />
            )}

            <div className='crm-client-tiles'>
                <SummaryTile
                    icon={ReceiptLongRoundedIcon}
                    label='Lançamentos'
                    value={summary.totalLancamentos}
                    hint='contas a receber no total'
                />
                <SummaryTile
                    icon={GavelRoundedIcon}
                    label='Multas em aberto'
                    value={formatCurrency(summary.multas)}
                    hint={`${summary.totalMultas} multas`}
                    tone={summary.multas > 0 ? 'is-danger' : ''}
                />
                <SummaryTile
                    icon={HandshakeRoundedIcon}
                    label='Acordos em aberto'
                    value={formatCurrency(summary.acordos)}
                    hint={`${summary.totalAcordos} parcelas`}
                />
            </div>

            <section className='crm-client-block'>
                <div className='crm-client-block-head'>
                    <h4 className='crm-client-block-title'>
                        <ReceiptLongRoundedIcon fontSize='inherit' />
                        Contas a receber
                        <span className='crm-client-block-count'>{receivables.total}</span>
                    </h4>

                    {statusOptions.length > 0 && (
                        <Autocomplete
                            multiple
                            disableCloseOnSelect
                            size='small'
                            limitTags={2}
                            className='crm-client-block-filter'
                            value={statusFilter}
                            options={statusOptions}
                            onChange={(event, values) => setStatusFilter(values)}
                            renderInput={(params) => (
                                <TextField {...params} label='Status' placeholder='Todos' />
                            )}
                        />
                    )}
                </div>

                {receivables.items.length === 0 && (
                    <p className='crm-client-block-empty'>Nenhuma conta a receber para este cliente.</p>
                )}

                {receivables.items.length > 0 && visibleReceivables.length === 0 && (
                    <p className='crm-client-block-empty'>Nenhum lançamento desta página com os status selecionados.</p>
                )}

                {visibleReceivables.length > 0 && (
                    <DataTable
                        columns={receivableColumns}
                        rows={visibleReceivables}
                        renderCell={(column, row) => renderReceivableCell(column, row, receivableActions)}
                        getRowKey={(row) => row.receber_id ?? `fatura-${row.fatura_id}`}
                        isScrollable
                    />
                )}

                {receivables.items.length > 0 && (
                    <FinancePager
                        page={receivables.page}
                        total={receivables.total}
                        pageSize={receivables.page_size}
                        onPageChange={onReceivablesPageChange}
                    />
                )}
            </section>

            <section className='crm-client-block'>
                <h4 className='crm-client-block-title'>
                    <GavelRoundedIcon fontSize='inherit' />
                    Multas
                    <span className='crm-client-block-count'>{penalties.length}</span>
                </h4>

                {penalties.length === 0
                    ? <p className='crm-client-block-empty'>Nenhuma multa registrada.</p>
                    : (
                        <DataTable
                            columns={simpleFinanceColumns}
                            rows={penalties}
                            renderCell={renderSimpleFinanceCell}
                            getRowKey={(row) => row.multa_id}
                        />
                    )}
            </section>

            <section className='crm-client-block'>
                <h4 className='crm-client-block-title'>
                    <HandshakeRoundedIcon fontSize='inherit' />
                    Acordos
                    <span className='crm-client-block-count'>{agreements.length}</span>
                </h4>

                {agreements.length === 0
                    ? <p className='crm-client-block-empty'>Nenhum acordo registrado.</p>
                    : (
                        <DataTable
                            columns={agreementColumns}
                            rows={agreements}
                            renderCell={renderSimpleFinanceCell}
                            getRowKey={(row) => row.acordo_id}
                        />
                    )}
            </section>
        </div>
    );
};
